import { memo, useCallback } from "react";

import { closeModal, useModalEntries } from "./store";
import type { ModalEntry } from "./types";

/**
 * Renders one stack entry. Split into its own component — and memoized — so
 * that flipping one entry's state does not re-run the render functions of the
 * unchanged sibling entries. Each entry also gets a stable `close` callback
 * bound to its own id.
 */
const ModalHostEntry = memo(
  ({ entry, showOverlay }: { entry: ModalEntry; showOverlay: boolean }) => {
    const close = useCallback(() => closeModal(entry.id), [entry.id]);
    return <>{entry.render({ id: entry.id, open: entry.open, close, showOverlay })}</>;
  },
);

ModalHostEntry.displayName = "ModalHostEntry";

/**
 * ModalHost — mounts the global modal stack. Mount it ONCE at the app root.
 *
 * Lifecycle per entry:
 *   open === true  -> rendered, enter animation plays
 *   close()        -> open = false, exit animation plays
 *   ~300ms later   -> entry removed from the store
 *
 * Only the bottom-most entry renders an overlay, so a stack of modals never
 * accumulates darkening. Each entry resolves its own surface independently.
 */
export const ModalHost = () => {
  const entries = useModalEntries();
  return (
    <>
      {entries.map((entry, index) => (
        <ModalHostEntry key={entry.id} entry={entry} showOverlay={index === 0} />
      ))}
    </>
  );
};

ModalHost.displayName = "ModalHost";
